import React, { Component, Fragment } from "react";
import moment from "moment";

import {
  CustomizedXAxisTick,
  CustomizedYAxisTick,
  renderIcons,
  truncate,
  Metric,
  selectFormat,
  Error,
} from "./../../../Utils/Utils.js";
import { XRayTooltip } from "./../../../Utils/XRayTooltip/XRayTooltip.js";

import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Brush,
  ResponsiveContainer,
} from "recharts";

import missing_image from "./../../../../i/missing_image.svg";
import numeral from "../../../../../node_modules/numeraljs/numeral.js";

import { getXrayTooltipPosition } from "../../../../helpers/DOM";

const COLORS = [
  "#F8DF84",
  "#1384C8",
  "#16DE85",
  "#D94720",
  "#75CFC0",
  "#F3108E",
  "#FBA2D3",
  "#FBA2D3",
  "#FEEDB1",
  "#16DE85",
  "#F3108E",
  "#F7A4D2",
  "#24DE85",
  "#19df85",
  "#F69484",
  "#5ECA6D",
  "#2FC2B0",
  "#A1DFC3",
];

const SCORE_COLORS = {
  A: "#FC6D52",
  B: "#FCD23A",
  C: "#B1C2C9",
};

const SCORE_COLORS_AREA = {
  A: "#FEEDE9",
  B: "#FEFAEA",
  C: "#F6F6F6",
};

class CustomToolTip extends Component {
  tooltipContent = (tooltip_data, content_data, time) => {
    return !tooltip_data.length ? null : (
      <Fragment>
        <header className="standard-tooltip__header">
          <span className="standard-tooltip__key-header">
            <b>
              {tooltip_data[0].payload.label}
              {tooltip_data[0].payload.label === 1 ? "h" : "hrs"}
            </b>{" "}
            Since Published
          </span>
        </header>
        <main className="standard-tooltip__main">
          {Object.keys(tooltip_data)
            .sort((a, b) => tooltip_data[b].value - tooltip_data[a].value)
            .map((key, index) => {
              return (
                <Fragment key={key}>
                  <div className="standard-tooltip__item">
                    <span
                      className="standard-tooltip__key"
                      style={{
                        color: tooltip_data[key].color,
                        textTransform: "none",
                      }}
                    >
                      <span
                        className="standard-tooltip__icon"
                        style={{ background: tooltip_data[key].color }}
                      />
                      {truncate(content_data[key].title, 25)}
                    </span>
                    <span className="standard-tooltip__value">
                      {selectFormat("number", tooltip_data[key].value)}
                    </span>
                  </div>
                </Fragment>
              );
            })}
        </main>
      </Fragment>
    );
  };

  render() {
    const { active, data } = this.props;
    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    if (active) {
      const { payload, time } = this.props;
      const tooltipData = payload.filter((e) => e.name.indexOf("post_") >= 0);
      if (payload && payload.length > 0) {
        return (
          <div className="standard-tooltip">
            {this.tooltipContent(tooltipData, data, time)}
          </div>
        );
      }
    }

    return null;
  }
}

class ContentVelocity extends Component {
  constructor(props) {
    super(props);
    this.state = {
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    this.closeTooltip();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      this.closeTooltip();
    }
  };

  renderLines = (data, num_lines) => {
    let lines = [];

    for (let i = 0; i < num_lines; i++) {
      lines.push(
        <Line
          key={i}
          type="monotone"
          dataKey={`post_${i}`}
          strokeWidth={2}
          stroke={COLORS[i]}
          dot={false}
        />
      );
    }

    return lines;
  };

  renderTable = (data) => {
    return data.map((item, index) => {
      // Views
      const VIEWS = item.views;

      // Ranges
      const RANGE_LOWER = item.predicted_lower_bound;
      // const RANGE_MID = item.range_mid
      const RANGE_UPPER = item.predicted_upper_bound;

      // Confidence
      const CONFIDENCE = item.confidence;

      // Icon
      const ICON = item.network ? renderIcons([item.network], "sm") : false;
      const ICON_SRC = ICON ? ICON[0].props.src : "";

      // Predicted Score Icon
      const PREDICTED_SCORE_ICON = item.predicted_score
        ? renderIcons([item.predicted_score + "_score"], "md")
        : "";

      // Image
      let IMAGE = { backgroundImage: `url(${missing_image})` };
      if (item.image == null) {
        IMAGE = { backgroundImage: `url(${missing_image})` };
      } else {
        IMAGE = { backgroundImage: `url(${item.image})` };
      }

      // Url
      const URL = item.url;

      // Title
      let TITLE = "";
      if (item.title !== null) {
        TITLE = (
          <h1 className="content-velocity__title">
            {truncate(item.title, 50)}
          </h1>
        );
      }

      const FIGURE_STYLES = {
        borderColor: COLORS[index],
      };

      const TIMESTAMP_LABEL = item.insight_ts
        ? `Last Prediction: ${moment(item.insight_ts).utc().fromNow()}`
        : "Not yet predicted";
      return (
        <div key={index} className="content-velocity__item mb-2">
          <div className="col mb-3">
            <div className="row">
              <div className="col mb-1">
                <time className="content-velocity__date">
                  Published <b> {moment(item.published_at).utc().fromNow()} </b>{" "}
                  on <img src={ICON_SRC} alt="Icon" />
                  <span className="content-table__timestamp">
                    &nbsp; &#9679; &nbsp; {TIMESTAMP_LABEL}
                  </span>
                </time>
              </div>
            </div>

            <article className="content-velocity__article">
              <figure
                className="content-velocity__figure"
                style={FIGURE_STYLES}
              >
                <a href={URL} target="_blank" rel="noopener noreferrer">
                  <div className="content-velocity__img" style={IMAGE} />
                </a>
              </figure>

              <section className="content-velocity__detail">
                <a href={URL} target="_blank" rel="noopener noreferrer">
                  {TITLE}
                </a>
              </section>
            </article>
          </div>

          <div className="col-12 pb-3 content-velocity__stats">
            <div className="row">
              <div className="col-auto col-md-3 col-lg-2 d-flex flex-row align-items-center">
                <Metric value={VIEWS} size={"xs"} /> &nbsp;{" "}
                <span className="content-velocity__label">views</span>
              </div>

              {item.predicted_score && (
                <Fragment>
                  <div className="col-auto col-md-3 col-lg-4 d-flex flex-row align-items-center">
                    <span className="content-velocity__label">
                      predicted score:
                    </span>
                    {PREDICTED_SCORE_ICON}
                    <span className="content-velocity__confidence">
                      {numeral(CONFIDENCE).format("%")}
                    </span>
                  </div>

                  <div className="col-12 col-md-6 col-lg-auto mt-2 mt-md-0 d-flex flex-row align-items-center">
                    <span className="content-velocity__label pr-1">
                      Predicted 30 Day UVs :{" "}
                    </span>
                    <span className="content-velocity__range">
                      {numeral(RANGE_LOWER).format("0.0a")}
                    </span>
                    {"  "} -{" "}
                    <span className="content-velocity__range">
                      {numeral(RANGE_UPPER).format("0.0a")}
                    </span>
                    <span className="content-velocity__confidence">
                      ({numeral(CONFIDENCE).format("%")})
                    </span>
                  </div>
                </Fragment>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  render() {
    const { data } = this.props;
    const time_frame = data.graph.length;

    let time = time_frame <= 24 && time_frame !== 7 ? "Hour" : "Day";

    return (
      <div className="col-12" ref={this.divPosition}>
        <div className="row">
          <div className="col mb-3">
            <ResponsiveContainer height={250}>
              <ComposedChart
                height={250}
                data={data.graph}
                margin={{
                  top: 20,
                  right: 0,
                  left: 0,
                  bottom: 0,
                }}
                padding={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 20,
                }}
                onClick={(d) => {
                  const { data } = this.props;
                  if (data.table && d !== null) {
                    const content_data = data.table;
                    d.content_data = content_data;
                    const indicator = "contentVelocity";
                    d.indicator = indicator;
                    this.setState({
                      tooltip: { ...d, show: true },
                    });
                  } else {
                    this.closeTooltip();
                  }
                }}
              >
                <Area
                  type="monotone"
                  dataKey="boundary_a"
                  stroke={SCORE_COLORS["A"]}
                  strokeWidth="0"
                  fill={SCORE_COLORS_AREA["A"]}
                />
                <Area
                  type="monotone"
                  dataKey="boundary_ba"
                  stroke={SCORE_COLORS["B"]}
                  strokeWidth="1"
                  strokeDasharray="2,2"
                  fill={SCORE_COLORS_AREA["B"]}
                />
                <Area
                  type="monotone"
                  dataKey="boundary_cb"
                  stroke={SCORE_COLORS["C"]}
                  strokeWidth="1"
                  strokeDasharray="2,2"
                  fill={SCORE_COLORS_AREA["C"]}
                />

                {!this.state.tooltip.show && (
                  <Tooltip
                    content={<CustomToolTip time={time} data={data.table} />}
                  />
                )}

                <CartesianGrid
                  stroke="#FEE5DE"
                  strokeDasharray="3 3"
                  horizontal={false}
                />

                {this.renderLines(data.graph, data.table.length)}

                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  scale="point"
                  tick={<CustomizedXAxisTick />}
                />

                <YAxis
                  axisLine={false}
                  tickLine={false}
                  mirror={true}
                  orientation="left"
                  domain={["dataMin", "dataMax"]}
                  tick={<CustomizedYAxisTick />}
                />

                <Brush
                  dataKey="label"
                  height={35}
                  stroke="#FDD9D1"
                  travellerWidth={10}
                />
              </ComposedChart>
            </ResponsiveContainer>
            {this.state.tooltip.activeCoordinate ? (
              <div
                className="xray-tooltip-container"
                style={{
                  top: "80px",
                  left:
                    this.state.tooltip.activeCoordinate.x + 1062 + 220 <
                    this.state.width
                      ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                      : this.state.tooltip.activeCoordinate.x -
                        getXrayTooltipPosition(
                          this.divPosition.current,
                          this.state.tooltip.activeCoordinate
                        ) +
                        "px",
                  opacity: this.state.tooltip.show ? 1.0 : 0.0,
                  visibility: this.state.tooltip.show ? "visible" : "hidden",
                }}
              >
                <XRayTooltip
                  data={this.state.tooltip}
                  close={this.closeTooltip.bind(this)}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div className="content-velocity__table">
          {this.renderTable(data.table)}
        </div>
      </div>
    );
  }
}

export default ContentVelocity;
